// La capa de datos: TanStack Query sobre el cliente tipado de @responia/api.
// Cada lectura se identifica por su ruta y sus parámetros; el directo (directo.ts) invalida
// por ruta, nunca refresca a ciegas.
import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, escribir, leerRuta } from "@responia/api/cliente";
import type { Lectura } from "@responia/api/esquemas";

export const consultas = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (n, e) => !(e instanceof ApiError && [401, 403, 404].includes(e.estado)) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
});

const GARDER_PENDANT_CHARGEMENT = new Set<Lectura>(["/analisis"]);

export function useLectura<L extends Lectura>(ruta: L, params?: Record<string, string>, activa = true) {
  return useQuery({
    queryKey: [ruta, params ?? {}],
    queryFn: () => leerRuta(ruta, params),
    enabled: activa,
    // Changer de période ou de filtre garde l'écran précédent pendant le chargement, sans squelette.
    // Jamais pour une conversation ou une fiche : on montrerait celle d'avant sous le nouveau nom.
    placeholderData: GARDER_PENDANT_CHARGEMENT.has(ruta) ? keepPreviousData : undefined,
  });
}

export function useEscritura<T extends Record<string, unknown>>(ruta: string, invalidar: Lectura[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: T) => escribir(ruta, cuerpo),
    onSuccess: () => Promise.all(invalidar.map((r) => qc.invalidateQueries({ queryKey: [r] }))),
  });
}

// Lo que cambia en el servidor → las lecturas que dependen de eso.
export const DEPENDENCIAS: Record<string, Lectura[]> = {
  conversaciones: ["/conversaciones", "/conversacion", "/hoy", "/contactos", "/contacto"],
  llamadas: ["/conversaciones", "/conversacion", "/hoy", "/contactos", "/contacto"],
  citas: ["/citas", "/hoy"],
  campanas: ["/campanas", "/campana"],
};
